import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Package, Plus, Edit, Trash2, DollarSign, Users, CheckCircle,
  XCircle, Clock, Search, Eye, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const FREQ_LABELS = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual',
  one_time: 'Pago Único'
};

const PackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSubsDialog, setShowSubsDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newItem, setNewItem] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', frequency: 'monthly', items: [], is_active: true, assigned_student_ids: []
  });

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/packages');
      setPackages(res.data);
    } catch (err) {
      toast.error('Error al cargar paquetes');
    }
    setLoading(false);
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await axios.get('/packages/subscriptions');
      setSubscriptions(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get('/students');
      setStudents(res.data.filter(s => s.is_active));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchPackages(); fetchSubscriptions(); fetchStudents(); }, [fetchPackages, fetchSubscriptions, fetchStudents]);

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', frequency: 'monthly', items: [], is_active: true, assigned_student_ids: [] });
    setNewItem('');
    setStudentSearch('');
  };

  const addItem = () => {
    if (newItem.trim()) {
      setFormData({ ...formData, items: [...formData.items, newItem.trim()] });
      setNewItem('');
    }
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Nombre y precio son requeridos');
      return;
    }
    try {
      await axios.post('/packages', {
        ...formData,
        price: parseFloat(formData.price),
        assigned_student_ids: formData.assigned_student_ids.length > 0 ? formData.assigned_student_ids : []
      });
      toast.success('Paquete creado exitosamente');
      setShowCreateDialog(false);
      resetForm();
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear paquete');
    }
  };

  const handleEdit = async () => {
    if (!selectedPackage) return;
    try {
      const update = {
        ...formData,
        price: parseFloat(formData.price),
        assigned_student_ids: formData.assigned_student_ids.length > 0 ? formData.assigned_student_ids : []
      };
      await axios.put(`/packages/${selectedPackage.id}`, update);
      toast.success('Paquete actualizado');
      setShowEditDialog(false);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`¿Eliminar PERMANENTEMENTE el paquete "${pkg.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`/packages/${pkg.id}`);
      toast.success('Paquete eliminado permanentemente');
      fetchPackages();
    } catch (err) {
      toast.error('Error al eliminar paquete');
    }
  };

  const handleMarkPaid = async (subId) => {
    try {
      await axios.put(`/packages/mark-paid/${subId}`);
      toast.success('Pago marcado como realizado');
      fetchSubscriptions();
    } catch (err) {
      toast.error('Error al marcar pago');
    }
  };

  const openEdit = (pkg) => {
    setSelectedPackage(pkg);
    setStudentSearch('');
    setFormData({
      name: pkg.name, description: pkg.description || '',
      price: String(pkg.price), frequency: pkg.frequency,
      items: pkg.items || [], is_active: pkg.is_active,
      assigned_student_ids: pkg.assigned_student_ids || (pkg.assigned_student_id ? [pkg.assigned_student_id] : [])
    });
    setShowEditDialog(true);
  };

  const filteredPackages = packages.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const normalizeSearch = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const availableStudents = students.filter(s => !formData.assigned_student_ids.includes(s.id) &&
    normalizeSearch(`${s.first_name} ${s.middle_name || ''} ${s.last_name_paternal} ${s.last_name_maternal || ''} ${s.student_number || ''} ${s.assigned_grade || ''}`)
      .includes(normalizeSearch(studentSearch.trim())));

  const getPackageSubs = (pkgId) => subscriptions.filter(s => s.package_id === pkgId);

  const packageFormJsx = (onSubmit, submitLabel) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-slate-600 text-xs">Nombre *</Label>
          <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ej: Paquete Premium" className="bg-white border-slate-200 text-slate-800"
            data-testid="package-name-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-slate-600 text-xs">Precio (US$) *</Label>
          <Input value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
            placeholder="0.00" type="number" step="0.01" className="bg-white border-slate-200 text-slate-800"
            data-testid="package-price-input" />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-slate-600 text-xs">Descripción</Label>
        <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Descripción del paquete..." className="bg-white border-slate-200 text-slate-800 min-h-[80px]"
          data-testid="package-description-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-slate-600 text-xs">Frecuencia de Pago</Label>
        <Select value={formData.frequency} onValueChange={(val) => setFormData(prev => ({ ...prev, frequency: val }))}>
          <SelectTrigger className="bg-white border-slate-200 text-slate-800" data-testid="package-frequency-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="monthly">Mensual</SelectItem>
            <SelectItem value="quarterly">Trimestral</SelectItem>
            <SelectItem value="semester">Semestral</SelectItem>
            <SelectItem value="annual">Anual</SelectItem>
            <SelectItem value="one_time">Pago Único</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Student Assignment - Multi-select */}
      <div className="space-y-1">
        <Label className="text-slate-600 text-xs flex items-center gap-1">
          <UserCheck className="w-3 h-3" /> Asignar a Estudiantes Específicos
        </Label>
        {formData.assigned_student_ids.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {formData.assigned_student_ids.map(sid => {
              const st = students.find(s => s.id === sid);
              return st ? (
                <Badge key={sid} className="bg-purple-50 text-purple-700 border-purple-200 cursor-pointer hover:bg-red-50 hover:text-red-600"
                  onClick={() => setFormData(prev => ({...prev, assigned_student_ids: prev.assigned_student_ids.filter(id => id !== sid)}))}>
                  {st.first_name} {st.last_name_paternal} <XCircle className="w-3 h-3 ml-1" />
                </Badge>
              ) : null;
            })}
          </div>
        )}
        <Label htmlFor="package-student-search" className="sr-only">Buscar estudiantes para asignar</Label>
        <Input id="package-student-search" value={studentSearch}
          onChange={e => setStudentSearch(e.target.value)}
          placeholder="Buscar por nombre, número o grado..."
          data-testid="assign-student-search" />
        <Select value="__placeholder__"
          onValueChange={(val) => {
            if (val && val !== '__placeholder__' && !formData.assigned_student_ids.includes(val)) {
              setFormData(prev => ({...prev, assigned_student_ids: [...prev.assigned_student_ids, val]}));
            }
          }}>
          <SelectTrigger className="bg-white border-slate-200 text-slate-800" data-testid="assign-student-select">
            <SelectValue placeholder="Agregar estudiante..." />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 max-h-60">
            {availableStudents.length === 0 && <p className="p-3 text-sm text-slate-500">No hay estudiantes que coincidan.</p>}
            {availableStudents.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.first_name} {s.last_name_paternal} {s.last_name_maternal || ''} · {s.student_number || ''} · {s.assigned_grade || ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-slate-400 mt-1">
          {formData.assigned_student_ids.length === 0
            ? 'Sin asignar = General (visible para todos los padres)'
            : `Asignado a ${formData.assigned_student_ids.length} estudiante(s). Solo sus padres verán este paquete.`}
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-600 text-xs">Elementos Incluidos</Label>
        <div className="flex gap-2">
          <Input value={newItem} onChange={(e) => setNewItem(e.target.value)}
            placeholder="Ej: Matrícula, Uniforme, Transporte..."
            className="bg-white border-slate-200 text-slate-800 flex-1"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
            data-testid="package-item-input" />
          <Button onClick={addItem} size="sm" variant="outline"
            className="border-slate-300 text-slate-600 hover:bg-orange-50 hover:text-orange-600">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.items.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.items.map((item, i) => (
              <Badge key={`${item}-${i}`} className="bg-slate-100 text-slate-700 border-slate-200 cursor-pointer hover:bg-red-50 hover:text-red-600"
                onClick={() => removeItem(i)}>
                {item} <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}
          className="border-slate-300 text-slate-600">Cancelar</Button>
        <Button onClick={onSubmit}
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
          data-testid="save-package-btn">{submitLabel}</Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Paquetes Escolares</h2>
          <p className="text-sm text-slate-500 mt-1">Administra los paquetes disponibles para los padres</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
          data-testid="create-package-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Paquete
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar paquetes..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
          data-testid="search-packages-input" />
      </div>

      {/* Packages Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : filteredPackages.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No hay paquetes creados</p>
            <p className="text-slate-400 text-sm mt-1">Crea tu primer paquete para los padres</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPackages.map((pkg) => {
            const subs = getPackageSubs(pkg.id);
            const activeSubs = subs.filter(s => s.status === 'active');
            const pendingPay = subs.filter(s => s.payment_status === 'pending' && s.status === 'active');
            return (
              <Card key={pkg.id} className={`bg-white border-slate-200 shadow-sm hover:shadow-md transition-all ${!pkg.is_active ? 'opacity-50' : ''}`}
                data-testid={`package-card-${pkg.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-slate-800 text-base">{pkg.name}</CardTitle>
                    <div className="flex items-center gap-1">
                      {(pkg.assigned_student_names?.length > 0 || pkg.assigned_student_id) && (
                        <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">
                          <UserCheck className="w-3 h-3 mr-1" />
                          {pkg.assigned_student_names?.length > 0
                            ? (pkg.assigned_student_names.length <= 2
                              ? pkg.assigned_student_names.join(', ')
                              : `${pkg.assigned_student_names[0]} +${pkg.assigned_student_names.length - 1}`)
                            : pkg.assigned_student_name || 'Asignado'}
                        </Badge>
                      )}
                      <Badge className={pkg.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                        {pkg.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>
                  {pkg.description && <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-orange-500">US$ {pkg.price.toLocaleString()}</span>
                    <span className="text-xs text-slate-500">/ {FREQ_LABELS[pkg.frequency] || pkg.frequency}</span>
                  </div>

                  {pkg.items && pkg.items.length > 0 && (
                    <div className="space-y-1">
                      {pkg.items.map((item, i) => (
                        <div key={`${pkg.id}-${item}-${i}`} className="flex items-center gap-2 text-xs text-slate-600">
                          <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-slate-700">{activeSubs.length}</p>
                      <p className="text-[10px] text-slate-500">Suscritos</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-amber-500">{pendingPay.length}</p>
                      <p className="text-[10px] text-slate-500">Pago Pendiente</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(pkg)}
                      className="flex-1 border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                      data-testid={`edit-package-${pkg.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => { setSelectedPackage(pkg); setShowSubsDialog(true); }}
                      className="flex-1 border-slate-300 text-slate-600 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-300"
                      data-testid={`view-subs-${pkg.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Suscritos
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(pkg)}
                        className="border-slate-300 text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                        data-testid={`delete-package-${pkg.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" /> Nuevo Paquete
            </DialogTitle>
          </DialogHeader>
          {packageFormJsx(handleCreate, "Crear Paquete")}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" /> Editar Paquete
            </DialogTitle>
          </DialogHeader>
          {packageFormJsx(handleEdit, "Guardar Cambios")}
        </DialogContent>
      </Dialog>

      {/* Subscriptions Dialog */}
      <Dialog open={showSubsDialog} onOpenChange={setShowSubsDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800">
              Suscripciones - {selectedPackage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedPackage && getPackageSubs(selectedPackage.id).length === 0 ? (
              <p className="text-slate-400 text-center py-8">No hay suscripciones a este paquete</p>
            ) : (
              selectedPackage && getPackageSubs(selectedPackage.id).map((sub) => (
                <div key={sub.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-800 font-medium">
                      {sub.parent_name || 'Padre'} {sub.family_wide ? '(Familiar)' : `- ${sub.student_name}`}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(sub.subscribed_at).toLocaleDateString('es-DO')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={sub.payment_status === 'paid'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {sub.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                    </Badge>
                    {sub.payment_status === 'pending' && sub.status === 'active' && (
                      <Button size="sm" onClick={() => handleMarkPaid(sub.id)}
                        className="bg-emerald-500 text-white hover:bg-emerald-600 h-7 text-xs"
                        data-testid={`mark-paid-${sub.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Marcar Pagado
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesTab;
